import React, { useRef, useEffect, useState } from "react";
import { SketchPicker } from "react-color";

interface ImageColourPickerProps {
  imageUrl: string;
}

const ImageColourPicker: React.FC<ImageColourPickerProps> = ({ imageUrl }) => {
  const [color, setColor] = useState<string>("#ffffff");
  const [threshold, setThreshold] = useState<number>(0.16);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const resultCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleClick = async (event: React.MouseEvent<HTMLImageElement>) => {
    if (!imgRef.current || !canvasRef.current || !resultCanvasRef.current)
      return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = imgRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    canvas.width = imgRef.current.width;
    canvas.height = imgRef.current.height;
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);

    const pixel = ctx.getImageData(x, y, 1, 1).data;
    setColor(`rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`);
    updateResultCanvas(`rgb(${pixel[0]}, ${pixel[1]}, ${pixel[2]})`);
  };

  const colorDistance = (color1: string, color2: string): number => {
    const match1 = color1.match(/\d+/g);
    const match2 = color2.match(/\d+/g);
    if (!match1 || !match2) return 1; // Return maximum distance if format is incorrect

    const rgb1 = match1.map(Number);
    const rgb2 = match2.map(Number);
    return (
      Math.sqrt(
        Math.pow(rgb2[0] - rgb1[0], 2) +
          Math.pow(rgb2[1] - rgb1[1], 2) +
          Math.pow(rgb2[2] - rgb1[2], 2)
      ) / 441.6729559300637
    ); // maximum possible distance
  };

  const updateResultCanvas = (selectedColor: string) => {
    if (!canvasRef.current || !resultCanvasRef.current) return;

    const sourceCanvas = canvasRef.current;
    const resultCanvas = resultCanvasRef.current;
    const sourceCtx = sourceCanvas.getContext("2d");
    const resultCtx = resultCanvas.getContext("2d");

    if (!sourceCtx || !resultCtx) return;

    resultCanvas.width = sourceCanvas.width;
    resultCanvas.height = sourceCanvas.height;

    const imageData = sourceCtx.getImageData(
      0,
      0,
      sourceCanvas.width,
      sourceCanvas.height
    );
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const pixelColor = `rgb(${r}, ${g}, ${b})`;

      const distance = colorDistance(pixelColor, selectedColor);
      const match = distance < threshold;

      data[i] = match ? 255 : 0;
      data[i + 1] = match ? 255 : 0;
      data[i + 2] = match ? 255 : 0;
    }

    resultCtx.putImageData(imageData, 0, 0);
  };

  useEffect(() => {
    if (color && canvasRef.current) {
      updateResultCanvas(color);
    }
  }, [color, threshold]);

  return (
    <div>
      <span>Лев, кликни курсором на картинке на пиксель где нужный цвет</span>
      <br />

      <img
        ref={imgRef}
        src={imageUrl}
        alt="Color Picker"
        onClick={handleClick}
        style={{ cursor: "crosshair" }}
      />
      <div style={{ marginTop: "10px" }}>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={threshold}
          onChange={(e) => setThreshold(parseFloat(e.target.value))}
        />
        <span>Threshold: {threshold}</span>
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <canvas
        ref={resultCanvasRef}
        style={{ marginTop: "10px", display: "block" }}
      />
      <div style={{ marginTop: "10px" }}>
        <SketchPicker
          color={color}
          onChangeComplete={(color) => setColor((color as any).rgb)}
        />
      </div>
    </div>
  );
};

export default ImageColourPicker;
